/**
 * The full column pass, phase 2 (`ColumnBindPasses.full`): the commit host
 * the reconciler drives (mount / recycle / remove / reconcile / markNew /
 * rebind with the membership-only keep / reflow), and the ONE memoized
 * skip-eligibility evaluation it and the referee share. Moved verbatim out of
 * `fullPass` (r5 FS 1).
 */
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { CellHeight, PlannedHeightPx } from "@tento-ui/components/sheet/cell-height";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { DomUtils } from "@tento-ui/dom";
import { $$ } from "@tento-ui/fastdom";
import type { CellDirective } from "./commit-bundle.ts";
import type { CommitHost } from "./dom-reconciler.ts";
import type { SkipEligibility } from "./skip-eligibility.ts";
import { cellDisplaysColumn, cellDisplaysRowAndColumn, cellStructureHonorable } from "./cell-identity.ts";
import type { ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";
import type { FullPassReconciled } from "./full-pass-reconcile.ts";

/** The commit host of one full pass, with the memoized terms the referee phase reads too. */
export type FullPassApply = {
  /** The decision-free host the reconciler applies the commit bundle against. */
  readonly host: CommitHost<SheetCell>;
  /** Whether a reflow may keep the cell's mounted content (memoized per row). */
  readonly cellReflowHonorable: (rowIndex: number) => boolean;
  /** Hand the host this pass's classified directives once they exist (`null`: no built scene). */
  setDirectives(directives: Option<readonly CellDirective[]>): void;
};

/** Phase 2 of the full pass: build the commit host over `input`'s cells. */
export function makeFullPassHost(
  port: ColumnBindPort,
  input: ColumnPassInput,
  reconciled: FullPassReconciled,
  membershipOnly: boolean,
): FullPassApply {
  const { sheet, defaultColumn, $header, $body, $footer, $cells, rows, widthPx, now, applyContext } = input;
  const { oldContentTypes, rowByKey, plans, bindings } = reconciled;
  // Per-cell directives, classified against the SELECTED structure + the
  // scene diff once both exist; read by the skip eligibility below.
  const state: { directives: Option<readonly CellDirective[]> } = { directives: null };
  // ── The ONE evaluation of "may this cell skip its rebind?" ──
  // Three terms gathered once per cell per pass and memoized for both consumers
  // (the referee pre-pass and applyCells' `skip`): SCENE (classifier said skip),
  // LEDGER (`shouldRebind === false`; dark LR6 refuses every row), DOM (rid,
  // column and planned height still on the occupant). Captured before any cell
  // write. Full rationale and the 2026-08-06 break: binder-history.md#one-evaluation.
  const reflowEligibilityByIndex = new Map<number, boolean>();
  const cellReflowHonorable = (rowIndex: number): boolean => {
    const memo = reflowEligibilityByIndex.get(rowIndex);
    if (memo != null) return memo;
    const $cell = nonnull($cells[rowIndex], "reflow: $cells[rowIndex]");
    const plan = nonnull(plans[rowIndex], "reflow: plans[rowIndex]");
    // Static height-only output has an identity but no plugin tree to preserve.
    // A pooled cell can also retain a detached content pointer.
    const hasMountedContent = $cell.$content != null && $cell.$content.parentElement === $cell;
    const honorable = cellDisplaysRowAndColumn(
      $cell, nonnull(rows[rowIndex], "reflow: rows[rowIndex]").id.toString(), defaultColumn,
    ) && (plan.skipContent || hasMountedContent);
    reflowEligibilityByIndex.set(rowIndex, honorable);
    return honorable;
  };
  const eligibilityByIndex = new Map<number, SkipEligibility>();
  const cellEligibility = (rowIndex: number): SkipEligibility => {
    const memo = eligibilityByIndex.get(rowIndex);
    if (memo != null) return memo;
    const $cell = $cells[rowIndex] ?? null;
    const row = rows[rowIndex] ?? null;
    const eligibility: SkipEligibility = {
      sceneUnchanged: state.directives?.[rowIndex]?.tag === "skip",
      ledgerUnchanged: row != null &&
        port.renderCursor()?.shouldRebind(row.id) === false,
      domHonorable: $cell != null && row != null &&
        $cell.getAttribute("rid") === row.id.toString() &&
        cellDisplaysColumn($cell, defaultColumn) &&
        plans[rowIndex] != null &&
        cellStructureHonorable($cell, plans[rowIndex], bindings[rowIndex] ?? null) &&
        // PLANNED vs CACHED, both branded — this axis is entitled to
        // the last bind's claim and to nothing more, because it runs per cell
        // per pass and must not force layout. `CachedHeightPx` and
        // `MeasuredHeightPx` have no overlap, so a future edit cannot quietly
        // "improve" this into a `getBoundingClientRect()` read here. The
        // pixels are audited where layout is already being read: the reveal
        // contract and the header sweep.
        CellHeight.honorsPlan(
          PlannedHeightPx.ofOption(plans[rowIndex]?.layout?.cellHeightPx),
          $cell.cachedHeight,
        ),
    };
    eligibilityByIndex.set(rowIndex, eligibility);
    return eligibility;
  };

  // Cells this column's structure program removes, drained by ONE batched
  // mutate instead of one per cell. The batch is enqueued on the first
  // removal — synchronously before `reconcile` enqueues its own — so the
  // fastdom write queue still runs every `removeFromDom` ahead of
  // `reconcileChildren`, exactly as the per-cell mutates did.
  const pendingRemovals: SheetCell[] = [];
  const host: CommitHost<SheetCell> = {
    // Resolve the live occupant at DRAIN TIME (learning 2026-06-18); the
    // bundle carries only slots, never a cell reference.
    occupantAt: (slot) => nonnull($cells[slot], "commit host: the occupant at slot"),
    mount: (slot) => {
      $cells.splice(slot, 0, port.mountCell());
    },
    recycle: (slot) => {
      if (!port.applyCurrent(applyContext)) return;
      // `oldContentTypes` is indexed by the ORIGINAL (unspliced) source
      // position, exactly as the inline loop did — kept for
      // `notifyCellRecycled`'s plugin-id fallback (recycle clears DOM, not JS
      // fields: learning 2026-02-15).
      port.notifyCellRecycled(oldContentTypes[slot] ?? null, nonnull($cells[slot], "recycle: the occupant at slot"));
    },
    remove: (slot) => {
      if (!port.applyCurrent(applyContext)) return;
      const $cell = nonnull($cells[slot], "remove: the occupant at slot");
      port.notifyCellRecycled(oldContentTypes[slot] ?? null, $cell);
      if (!port.applyCurrent(applyContext)) return;
      // Reset height synchronously (OUTSIDE the mutate) so a stale height
      // can't perturb scroll math before the async DOM removal — the
      // phantom-gap guard; `removeFromDom` runs INSIDE the mutate.
      $cell.style.setProperty("--height", "0px");
      $cell.height = 0;
      if (pendingRemovals.length === 0) {
        $$.mutate(() => {
          if (!port.applyCurrent(applyContext)) return;
          for (const $removed of pendingRemovals) {
            DomUtils.removeFromDom($removed, false);
          }
          pendingRemovals.length = 0;
        });
      }
      pendingRemovals.push($cell);
      $cells.splice(slot, 1);
    },
    reconcile: () => {
      if (!port.applyCurrent(applyContext)) return;
      $$.mutate(() => {
        if (!port.applyCurrent(applyContext)) return;
        const headerCells: SheetCell[] = [];
        const bodyCells: SheetCell[] = [];
        const footerCells: SheetCell[] = [];

        for (const [idx, $cell] of $cells.entries()) {
          const row = rows[idx];
          const where = row ? sheet.joinedTree.getRowLocation(row.id) : null;
          switch (where) {
            case "header": headerCells.push($cell); break;
            case "footer": footerCells.push($cell); break;
            default:       bodyCells.push($cell); break;
          }
        }

        SheetCell.reconcileChildren($header, headerCells);
        SheetCell.reconcileChildren($body, bodyCells);
        if ($footer) SheetCell.reconcileChildren($footer, footerCells);
      });
    },
    markNew: (key) => {
      if (!port.applyCurrent(applyContext)) return;
      const row = rowByKey.get(key);
      if (row == null) return;
      // markNew's `previousRenderState` write is RETIRED on the live path:
      // plans are computed BEFORE markNew runs (the phase order above) and
      // `updatePreviousState` overwrites this row at pass end, so the write
      // only ever moved `updateType`/`diff` — fields with ZERO DOM consumers
      // (pinned inert by sheets/tests/commit-bundle/marknew-plans-signal.deno.test.ts).
      // LR6: a fresh / recycled cell holds no valid rendered-version record —
      // drop it so the row can never skip its first bind into this slot
      // (mirrors the `isNew` reset; recycling-reset correctness).
      port.renderCursor()?.evict(row.id);
    },
    rebind: ($cell, rowIndex) => {
      if (!port.applyCurrent(applyContext)) return;
      // F10: a membership-only pass keeps a resident cell that still shows
      // its row and column at the planned height with mounted content; it
      // was bound since the last content change and only the viewport moved.
      // Entering rows (fresh or recycled cells) fail these checks and bind.
      // Read fresh from the occupant now (the memoized skip terms were
      // captured against the pre-structure slots).
      const keptRow = rows[rowIndex];
      const keptPlan = plans[rowIndex];
      const completed = port.completedBind($cell);
      if (
        membershipOnly && keptRow != null && keptPlan != null && completed != null &&
        // The cell's last full rebind completed, in this context, for this
        // row, at the plan's past/current state (N3-3).
        completed.epoch === port.contextEpoch() &&
        completed.rowId === keptRow.id.toString() &&
        completed.inThePast === keptPlan.timeState.inThePast &&
        completed.isCurrent === keptPlan.timeState.isCurrent &&
        cellDisplaysRowAndColumn($cell, keptRow.id.toString(), defaultColumn) &&
        CellHeight.honorsPlan(PlannedHeightPx.ofOption(plans[rowIndex]?.layout?.cellHeightPx), $cell.cachedHeight) &&
        $cell.$content != null && $cell.$content.parentElement === $cell
      ) {
        port.noteMembershipKeep();
        return;
      }
      port.applyPlanFullRebind(
        $cell,
        nonnull(plans[rowIndex], "rebind: plans[rowIndex]"),
        nonnull(rows[rowIndex], "rebind: rows[rowIndex]"),
        sheet,
        defaultColumn,
        widthPx,
        now,
        nonnull(plans[rowIndex], "semantic plan index").rowIndex,
        bindings[rowIndex] ?? null,
        applyContext,
      );
    },
    reflow: ($cell, rowIndex) => {
      if (!port.applyCurrent(applyContext)) return;
      port.applyPlanReflow(
        $cell,
        nonnull(plans[rowIndex], "reflow: plans[rowIndex]"),
        nonnull(rows[rowIndex], "reflow: rows[rowIndex]"),
        sheet,
        defaultColumn,
        widthPx,
        now,
        nonnull(plans[rowIndex], "semantic plan index").rowIndex,
        bindings[rowIndex] ?? null,
        cellReflowHonorable(rowIndex),
        applyContext,
      );
    },
    // The ONE decider's input. `canSkip` used to conjoin the
    // four axes here itself, and the referee pre-pass re-implemented three
    // of them verbatim as `honorableOnDom`. Both are gone: the host now
    // GATHERS the terms into a `SkipEligibility` and `decideSkip` is the
    // only place they are turned into an answer.
    eligibility: (rowIndex) => cellEligibility(rowIndex),
  };


  return {
    host,
    cellReflowHonorable,
    setDirectives: (directives) => {
      state.directives = directives;
    },
  };
}
